/**
 * Status badges.
 *
 * Every badge is a coloured pill *plus a word* — never a bare colour — and now
 * also a *shape*. Each tone draws a different mark: a filled disc for good, a
 * triangle for warning, a diamond for serious, a square for critical, a hollow
 * ring for neutral. Colour alone fails in greyscale, under forced-colors, and
 * for the ~8% of men with a red-green deficiency, which on a console whose
 * entire job is "is this healthy" is not an acceptable failure.
 *
 * The exported surface is unchanged from the Carbon version, so every page that
 * imports these follows the redesign without an edit.
 */

import type { ReactNode } from 'react';

import type { ServiceState, Severity } from '../types/api';

export type Tone = 'good' | 'warning' | 'serious' | 'critical' | 'neutral' | 'info';

/** The shape channel. Distinct silhouettes, not decorations. */
const TONE_MARK: Record<Tone, ReactNode> = {
  good: <circle cx="5" cy="5" r="4" />,
  warning: <path d="M5 .8 9.6 9H.4Z" />,
  serious: <path d="M5 .6 9.4 5 5 9.4.6 5Z" />,
  critical: <rect x="1" y="1" width="8" height="8" rx="1" />,
  info: <circle cx="5" cy="5" r="4" />,
  neutral: <circle cx="5" cy="5" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.6" />,
};

function Mark({ tone }: { tone: Tone }) {
  return (
    <svg className="badge__mark" viewBox="0 0 10 10" width="10" height="10" fill="currentColor" aria-hidden="true" focusable="false">
      {TONE_MARK[tone]}
    </svg>
  );
}

export function Badge({
  tone = 'neutral',
  children,
  small = false,
  title,
}: {
  tone?: Tone;
  children: ReactNode;
  small?: boolean;
  title?: string;
}) {
  return (
    <span
      className={`badge badge--${tone}${small ? ' badge--sm' : ''}`}
      title={title}
    >
      <Mark tone={tone} />
      <span className="badge__label">{children}</span>
    </span>
  );
}

const SERVICE_STATE_TONE: Record<string, Tone> = {
  up: 'good',
  down: 'critical',
  degraded: 'serious',
  unknown: 'neutral',
};

const SERVICE_STATE_LABEL: Record<string, string> = {
  up: 'Up',
  down: 'Down',
  degraded: 'Degraded',
  unknown: 'Unknown',
};

export function StatusBadge({ state }: { state: ServiceState }) {
  const tone = SERVICE_STATE_TONE[state] ?? 'neutral';
  const label = SERVICE_STATE_LABEL[state] ?? state;
  return <Badge tone={tone}>{label}</Badge>;
}

const SEVERITY_TONE: Record<string, Tone> = {
  block: 'critical',
  warn: 'warning',
  info: 'info',
};

const SEVERITY_LABEL: Record<string, string> = {
  block: 'Blocking',
  warn: 'Warning',
  info: 'Info',
};

export function severityTone(severity: Severity): Tone {
  return SEVERITY_TONE[severity] ?? 'neutral';
}

export function severityLabel(severity: Severity): string {
  return SEVERITY_LABEL[severity] ?? severity;
}

export function SeverityBadge({
  severity,
  small = false,
}: {
  severity: Severity;
  small?: boolean;
}) {
  return (
    <Badge tone={severityTone(severity)} small={small}>
      {severityLabel(severity)}
    </Badge>
  );
}

/**
 * A tri-state boolean. `null` is rendered as its own word, never folded into
 * the false case: "not checked" and "missing" call for different actions.
 */
export function TriStateBadge({
  value,
  trueLabel,
  falseLabel,
  nullLabel = 'Not checked',
  trueTone = 'good',
  falseTone = 'critical',
}: {
  value: boolean | null | undefined;
  trueLabel: string;
  falseLabel: string;
  nullLabel?: string;
  trueTone?: Tone;
  falseTone?: Tone;
}) {
  if (value === null || value === undefined) {
    return <Badge tone="neutral">{nullLabel}</Badge>;
  }
  return (
    <Badge tone={value ? trueTone : falseTone}>
      {value ? trueLabel : falseLabel}
    </Badge>
  );
}

/** A plain, non-status label: counts, ports, identifiers. No mark — nothing to signal. */
export function Tag({
  children,
  mono = false,
  title,
}: {
  children: ReactNode;
  mono?: boolean;
  title?: string;
}) {
  return (
    <span className={`chip${mono ? ' chip--mono' : ''}`} title={title}>
      {children}
    </span>
  );
}
